import type { Experiment } from 'experiment';
import type { EditorModel } from 'types/core';
import type { MigrationFlow } from '@/sectionsMigration';

import { TEMPLATES_BLACK_LIST } from './templatesBlackList';

const MAX_PAGES_TO_MIGRATE = 50;

const isTemplateMigrationFlow = ({
  experiment,
  editorModel,
}: {
  experiment: Experiment;
  editorModel: EditorModel;
}): boolean => {
  const isExperimentEnabled =
    experiment.isOpen('se_sectionsMigration') ||
    experiment.isOpen('se_sectionsMigrationEditor');
  const isNewSite = editorModel.isDraft || editorModel.firstSave;
  const isBlackListed = TEMPLATES_BLACK_LIST.has(
    editorModel.siteHeader.originalTemplateId,
  );

  return (
    (isExperimentEnabled && isNewSite && !isBlackListed) ||
    experiment.isOpen('se_sectionsForceMigration')
  );
};

const isADIMigrationFlow = ({
  experiment,
  editorModel,
  queryUtil,
}: {
  experiment: Experiment;
  editorModel: EditorModel;
  queryUtil: any;
}): boolean =>
  (editorModel.onboardingEditorUrl && // assume it is equal to editorAPI.generalInfo.isSiteFromOnBoarding()
    queryUtil.isParameterTrue('onboarding') && // need it because onboardingEditorUrl still exist after first editor session
    queryUtil.isParameterTrue('sectionsMigration')) ||
  (experiment.isOpen('se_sectionsMigrationEditor') &&
    editorModel.onboardingEditorUrl &&
    queryUtil.isParameterTrue('onboarding')) ||
  experiment.isOpen('se_sectionsForceMigrationADI');

const isEditorMigrationFlow = ({
  experiment,
  editorModel,
  queryUtil,
}: {
  experiment: Experiment;
  editorModel: EditorModel;
  queryUtil: any;
}): boolean => {
  const { permissionsInfo } = editorModel;
  const isUserOwner =
    permissionsInfo &&
    permissionsInfo.ownerId === permissionsInfo.loggedInUserId;

  return (
    (isUserOwner ||
      experiment.isOpen('se_sectionsMigrationNonOwner') ||
      queryUtil.isParameterTrue('forceSectionsMigration')) &&
    (experiment.isOpen('se_sectionsMigrationEditor') ||
      queryUtil.isParameterTrue('forceSectionsMigration'))
  );
};

const isHeavySiteMigrationFlow = ({
  experiment,
  editorModel,
  queryUtil,
}: {
  experiment: Experiment;
  editorModel: EditorModel;
  queryUtil: any;
}): boolean => {
  const isHeavySite =
    editorModel.siteHeader.pageIdList.pages.length >= MAX_PAGES_TO_MIGRATE;

  return (
    (isHeavySite && experiment.isOpen('se_heavySitesMigrationLoader')) ||
    queryUtil.isParameterTrue('forceSectionsMigration')
  );
};

export function getSectionsMigrationFlow({
  isInsideEditorX,
  isInsideAppStudio,
  experiment,
  editorModel,
  queryUtil,
}: {
  isInsideEditorX: boolean;
  isInsideAppStudio: boolean;
  experiment: Experiment;
  editorModel: EditorModel;
  queryUtil: any;
}): MigrationFlow {
  if (
    isInsideAppStudio ||
    isInsideEditorX ||
    experiment.isOpen('se_kill_sections') ||
    editorModel.isImpersonated
  ) {
    return;
  }

  let migrationFlow: MigrationFlow;

  switch (true) {
    case isTemplateMigrationFlow({ experiment, editorModel }):
      migrationFlow = <MigrationFlow>'template';
      break;
    case isADIMigrationFlow({ experiment, editorModel, queryUtil }):
      migrationFlow = <MigrationFlow>'adi';
      break;
    case isEditorMigrationFlow({ experiment, editorModel, queryUtil }):
      if (isHeavySiteMigrationFlow({ experiment, editorModel, queryUtil })) {
        migrationFlow = <MigrationFlow>'heavySite';
      } else {
        migrationFlow = <MigrationFlow>'editor';
      }
      break;
  }

  return migrationFlow;
}
